import React, { useRef, useEffect } from 'react';
import { X, Copy, Trash2 } from 'lucide-react';
import { useWorldStore, LightItem, LightType } from '../stores/worldStore';
import { useDraggable } from '../hooks/useDraggable';
import './LightControls.css';

const LIGHT_TYPE_LABELS: Record<LightType, string> = {
  spotlight: 'Spotlight',
  pointlight: 'Point Light',
  arealight: 'Area Light',
};

interface LightControlsProps {
  light: LightItem;
  onUpdate: (updates: Partial<LightItem>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onClose: () => void;
}

const LightControls: React.FC<LightControlsProps> = ({ light, onUpdate, onDelete, onDuplicate, onClose }) => {
  const { dragStyle, onPointerDown } = useDraggable();
  const panelRef = useRef<HTMLDivElement>(null);

  // Block ALL native pointer/mouse/wheel events from reaching the canvas/OrbitControls
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const stop = (e: Event) => e.stopPropagation();
    const events = ['pointerdown', 'pointermove', 'pointerup', 'mousedown', 'mousemove', 'mouseup', 'wheel', 'touchstart', 'touchmove', 'touchend'];
    events.forEach((evt) => el.addEventListener(evt, stop, { capture: true }));
    return () => { events.forEach((evt) => el.removeEventListener(evt, stop, { capture: true })); };
  }, []);

  const posSlider = (axis: 0 | 1 | 2, label: string) => (
    <div className="control-row" key={label}>
      <span>{label}</span>
      <input type="range" min={-30} max={30} step={0.5} value={light.position[axis]}
        onChange={(e) => {
          const pos = [...light.position] as [number, number, number];
          pos[axis] = +e.target.value;
          onUpdate({ position: pos });
        }} />
      <span className="value">{light.position[axis].toFixed(1)}</span>
    </div>
  );

  const rotSlider = (axis: 0 | 1 | 2, label: string) => (
    <div className="control-row" key={label}>
      <span>{label}</span>
      <input type="range" min={-3.14} max={3.14} step={0.05} value={light.rotation[axis]}
        onChange={(e) => {
          const rot = [...light.rotation] as [number, number, number];
          rot[axis] = +e.target.value;
          onUpdate({ rotation: rot });
        }} />
      <span className="value">{(light.rotation[axis] * (180 / Math.PI)).toFixed(0)}°</span>
    </div>
  );

  return (
    <div ref={panelRef} className="light-controls" style={dragStyle} onPointerDown={(e) => { e.stopPropagation(); onPointerDown(e); }} onMouseDown={(e) => e.stopPropagation()}>
      <div className="controls-header" data-drag-handle>
        <h3>{light.name}</h3>
        <button className="icon-btn" onClick={onClose} title="Close"><X size={18} /></button>
      </div>

      <div className="controls-scroll" onPointerDown={(e) => e.stopPropagation()}>
        {/* Light Type */}
        <div className="control-group">
          <label>Type</label>
          <div className="toggle-row">
            {(Object.keys(LIGHT_TYPE_LABELS) as LightType[]).map((t) => (
              <button key={t}
                className={`toggle-btn ${light.type === t ? 'active' : ''}`}
                onClick={() => onUpdate({ type: t, name: LIGHT_TYPE_LABELS[t] })}
              >{LIGHT_TYPE_LABELS[t]}</button>
            ))}
          </div>
        </div>

        {/* Color & Intensity */}
        <div className="control-group">
          <label>Appearance</label>
          <div className="control-row">
            <span>Color</span>
            <input type="color" value={light.color} onChange={(e) => onUpdate({ color: e.target.value })} />
          </div>
          <div className="control-row">
            <span>Power</span>
            <input type="range" min={0} max={10} step={0.1} value={light.intensity}
              onChange={(e) => onUpdate({ intensity: +e.target.value })} />
            <span className="value">{light.intensity.toFixed(1)}</span>
          </div>
          <div className="control-row">
            <span>Range</span>
            <input type="range" min={1} max={100} step={1} value={light.distance}
              onChange={(e) => onUpdate({ distance: +e.target.value })} />
            <span className="value">{light.distance}</span>
          </div>
        </div>

        {/* Spotlight-specific */}
        {light.type === 'spotlight' && (
          <div className="control-group">
            <label>Spotlight</label>
            <div className="control-row">
              <span>Angle</span>
              <input type="range" min={0.1} max={1.5} step={0.05} value={light.angle}
                onChange={(e) => onUpdate({ angle: +e.target.value })} />
              <span className="value">{(light.angle * (180 / Math.PI)).toFixed(0)}°</span>
            </div>
            <div className="control-row">
              <span>Soft</span>
              <input type="range" min={0} max={1} step={0.05} value={light.penumbra}
                onChange={(e) => onUpdate({ penumbra: +e.target.value })} />
              <span className="value">{light.penumbra.toFixed(2)}</span>
            </div>
          </div>
        )}

        {/* Area light specific */}
        {light.type === 'arealight' && (
          <div className="control-group">
            <label>Area Size</label>
            <div className="control-row">
              <span>Width</span>
              <input type="range" min={0.5} max={20} step={0.5} value={light.width}
                onChange={(e) => onUpdate({ width: +e.target.value })} />
              <span className="value">{light.width.toFixed(1)}</span>
            </div>
            <div className="control-row">
              <span>Height</span>
              <input type="range" min={0.5} max={20} step={0.5} value={light.height}
                onChange={(e) => onUpdate({ height: +e.target.value })} />
              <span className="value">{light.height.toFixed(1)}</span>
            </div>
          </div>
        )}

        {/* Position */}
        <div className="control-group">
          <label>Position</label>
          {posSlider(0, 'X')}
          {posSlider(1, 'Y')}
          {posSlider(2, 'Z')}
        </div>

        {/* Rotation (spotlight/arealight) */}
        {light.type !== 'pointlight' && (
          <div className="control-group">
            <label>Rotation</label>
            {rotSlider(0, 'X')}
            {rotSlider(1, 'Y')}
            {rotSlider(2, 'Z')}
          </div>
        )}

        {/* Shadow toggle */}
        <div className="control-group">
          <label>Options</label>
          <div className="control-row">
            <span>Shadow</span>
            <button
              className={`toggle-switch ${light.castShadow ? 'on' : ''}`}
              onClick={() => onUpdate({ castShadow: !light.castShadow })}
            >
              <span className="toggle-knob" />
            </button>
          </div>
          <div className="control-row">
            <span>Visible</span>
            <button
              className={`toggle-switch ${light.visible ? 'on' : ''}`}
              onClick={() => onUpdate({ visible: !light.visible })}
            >
              <span className="toggle-knob" />
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="light-actions">
          <button className="action-btn-lg" onClick={onDuplicate}><Copy size={14} /> Duplicate</button>
          <button className="action-btn-lg delete" onClick={onDelete}><Trash2 size={14} /> Delete</button>
        </div>
      </div>
    </div>
  );
};

export default LightControls;
